import { observer } from 'mobx-react';
import ComparableCompanyDataRow from './ComparableCompanyDataRow';
import HeatmapControls from './HeatmapControls';
import ComparableCompaniesActionButtons from './ComparableCompaniesActionButtons';
import { getComparableCompaniesGridTemplateColumns } from './comparableCompaniesGrid';

/**
 * ComparableCompaniesHeader - Desktop header for comparable companies section
 *
 * Conditionally renders:
 * - Column headers for list view
 * - Heatmap controls for heatmap view
 * - Action buttons (save project, switch view, sort) via shared component
 * - Comparable data row (target company) for list view - inside header wrapper for correct border placement
 *
 * Uses MobX observer to react to state changes for menus and toggles.
 */
const ComparableCompaniesHeader = observer(
  ({ state, viewsList, sortOptions, project, profilerAccessStore, comparableData }) => {
    const hasExtendedAccess = profilerAccessStore.canViewProfilerDetailsExtendedData(project);
    // TD-2499: CAGR 3Y is visible to Lite (details access); EBIT + EBIT-margin stay Full-only.
    const hasDetailsAccess = profilerAccessStore.canViewProfilerDetails(project);

    return (
      <div
        className="results-header-wrap"
        style={
          state.selectedSimilarCompaniesView !== viewsList[0].value
            ? {
                paddingBottom: 0
              }
            : {}
        }
      >
        <div
          className="results-header"
          style={
            state.layout === 'desktop'
              ? {
                  gridTemplateColumns: getComparableCompaniesGridTemplateColumns(
                    hasExtendedAccess,
                    hasDetailsAccess
                  ),
                  ...(state.selectedSimilarCompaniesView !== viewsList[0].value
                    ? {
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        backgroundColor: 'transparent'
                      }
                    : {})
                }
              : {}
          }
        >
          {/* Column headers for list view */}
          {state.selectedSimilarCompaniesView === viewsList[0].value && (
            <>
              <div className="col company-name">Company name</div>
              <div className="col headquarters">Location</div>
              <div className="col description">Description</div>
              <div className="col revenue">Sales (€M)</div>
              {hasDetailsAccess && <div className="col revenue-yoy">CAGR 3Y (%)</div>}
              {hasExtendedAccess && <div className="col ebit">EBIT (€M)</div>}
              {hasExtendedAccess && <div className="col ebit-margin">EBIT Margin (%)</div>}
              <div className="col employees">Employees</div>
            </>
          )}

          {/* Heatmap controls for heatmap view - desktop */}
          {state.selectedSimilarCompaniesView === viewsList[1].value &&
            state.layout === 'desktop' && <HeatmapControls state={state} />}

          {/* Action buttons */}
          <div className="col actions">
            <ComparableCompaniesActionButtons
              state={state}
              viewsList={viewsList}
              sortOptions={sortOptions}
              hasExtendedAccess={hasExtendedAccess}
              idSuffix="desktop"
              filterViewsFn={(view) =>
                hasExtendedAccess || view.value !== viewsList[2].value
              }
            />
          </div>
        </div>

        {/* Comparable data row - rendered inside results-header-wrap so border-bottom appears after it */}
        {state.selectedSimilarCompaniesView === viewsList[0].value && !!comparableData && (
          <ComparableCompanyDataRow
            comparableData={comparableData}
            layout={state.layout}
            project={project}
            profilerAccessStore={profilerAccessStore}
          />
        )}
      </div>
    );
  }
);

export default ComparableCompaniesHeader;
